import { getCurrentPlatformTheme } from '@/helpers/platformDetection';
import { setLocaleBeforeRouteChange } from '@/i18n/setup/i18nSetup';
import type { AppState } from '@/store/types';
import { setupRumViewTracking } from '@smg-real-estate/browser-rum/vue';
import type { HgSupportedLanguage } from '@smg-real-estate/domus';
import type { I18n } from 'vue-i18n';
import {
  createMemoryHistory,
  createRouter,
  createWebHistory,
  type Router,
  type RouteRecordRaw,
} from 'vue-router';
import { type Store } from 'vuex';
import generateLayoutSolutionRoutes from './layoutSolutionRoutes';
import generatePageNotFoundRoutes from './pageNotFoundRoute';

export function getAllRoutes(store: Store<AppState>): RouteRecordRaw[] {
  const platform = getCurrentPlatformTheme();
  return [...generateLayoutSolutionRoutes(store), ...generatePageNotFoundRoutes(platform)];
}

export function initGuards(router: Router, i18n: I18n): void {
  router.beforeEach(async (to, _from, next) => {
    const locale = (to.params.locale || to?.meta?.locale) as HgSupportedLanguage;

    await setLocaleBeforeRouteChange(i18n, locale);

    next();
  });
  setupRumViewTracking(router);
}

export function createAppRouter(store: Store<AppState>): Router {
  const BASE_PATH = import.meta.env.VUE_BASE_PATH;

  return createRouter({
    history: import.meta.env.SSR ? createMemoryHistory(BASE_PATH) : createWebHistory(BASE_PATH),
    routes: getAllRoutes(store),
    scrollBehavior(to, _from, savedPosition) {
      // Logic taken from https://router.vuejs.org/guide/advanced/scroll-behavior.html

      // Simulate the "scroll to anchor" behavior
      if (to.hash) {
        return {
          el: to.hash,
          behavior: 'smooth',
        };
      }

      // Return the savedPosition for a native-like behavior when navigating with back/forward buttons,
      if (savedPosition) {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(savedPosition);
          }, 100);
        });
      }

      // Otherwise, scroll to top for all route navigations
      return { top: 0, left: 0 };
    },
  });
}
